// Thin client for the /assessments API.
//
// Errors carry `status`, with 0 meaning the request never reached the server
// (offline, DNS, CORS, timeout). The sync layer decides what each one means.

const API_URL = process.env.REACT_APP_API_URL;
const TIMEOUT_MS = 20000;

export class ApiError extends Error {
  constructor(message, status, body) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

async function call(path, { method = 'GET', body } = {}) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  let response;
  try {
    response = await fetch(`${API_URL}${path}`, {
      method,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('wmf_token')}`,
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });
  } catch (err) {
    throw new ApiError('Could not reach the server', 0);
  } finally {
    clearTimeout(timer);
  }
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new ApiError(data.msg || `Request failed (${response.status})`, response.status, data);
  }
  return data;
}

// For non-JSON traffic: file uploads (FormData) and PDF/file downloads (Blob).
async function callRaw(path, { method = 'GET', body } = {}) {
  let response;
  try {
    response = await fetch(`${API_URL}${path}`, {
      method,
      headers: { Authorization: `Bearer ${localStorage.getItem('wmf_token')}` },
      body,
    });
  } catch {
    throw new ApiError('Could not reach the server', 0);
  }
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new ApiError(data.msg || `Request failed (${response.status})`, response.status, data);
  }
  return response;
}

export const assessmentApi = {
  schema: (code, version) => call(`/assessments/schema/${code}${version ? `?version=${version}` : ''}`),
  list: (params = {}) => {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v)).toString();
    return call(`/assessments${qs ? `?${qs}` : ''}`);
  },
  get: (uuid) => call(`/assessments/${uuid}`),
  create: (payload) => call('/assessments', { method: 'POST', body: payload }),
  changes: (uuid, changes) => call(`/assessments/${uuid}/changes`, { method: 'POST', body: { changes } }),
  complete: (uuid) => call(`/assessments/${uuid}/complete`, { method: 'POST' }),
  reopen: (uuid) => call(`/assessments/${uuid}/reopen`, { method: 'POST' }),
  cancel: (uuid, reason) => call(`/assessments/${uuid}/cancel`, { method: 'POST', body: { reason } }),
  surveyStatus: (uuid) => call(`/assessments/${uuid}/survey-status`),
  surveyLink: (uuid) => call(`/assessments/${uuid}/survey-link`, { method: 'POST' }),
  memoPreview: (uuid) => callRaw(`/assessments/${uuid}/memo/preview`).then((r) => r.blob()),
  formPreview: (uuid) => callRaw(`/assessments/${uuid}/form/preview`).then((r) => r.blob()),
  memoFile: (uuid) => callRaw(`/assessments/${uuid}/memo/file`).then((r) => r.blob()),
  uploadMemo: (uuid, file) => {
    const body = new FormData();
    body.append('file', file);
    return callRaw(`/assessments/${uuid}/memo/file`, { method: 'POST', body }).then((r) => r.json());
  },
  // POST rather than DELETE: some hosting setups refuse DELETE requests.
  removeMemo: (uuid) => call(`/assessments/${uuid}/memo/file/remove`, { method: 'POST' }),
  submit: (uuid, confirmDuplicate = false) => call(`/assessments/${uuid}/submit`, { method: 'POST', body: { confirmDuplicate } }),
  surveyOverride: (uuid, note) => call(`/assessments/${uuid}/survey-override`, { method: 'POST', body: { note } }),
  removeSurveyOverride: (uuid) => call(`/assessments/${uuid}/survey-override/remove`, { method: 'POST' }),
  document: (documentId) => callRaw(`/session-reports/documents/${documentId}`).then((r) => r.blob()),
  deviceKey: () => call('/assessments/device-key'),
  offlineStudents: () => call('/assessments/offline-students'),
  deviceEvent: (deviceId, body) => call(`/assessments/devices/${deviceId}/events`, { method: 'POST', body }),
  offlineAccess: () => call('/assessments/offline-access'),
  setOfflineAccess: (userId, enabled) => call(`/assessments/offline-access/${userId}`, { method: 'PUT', body: { enabled } }),
};

export const staffApi = {
  myStudents: () => call('/therapists/my-students'),
  therapists: () => call('/therapists'),
  therapistStudents: (therapistId) => call(`/therapists/${therapistId}/students`),
};
